<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import EditsAPI from 'dashboard/api/whatsappMessageEdits';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  messageId: { type: Number, required: true },
  content: { type: String, required: true },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const dialog = ref(null);
const draft = ref(props.content);
const saving = ref(false);
const submitted = ref(false);
const error = ref('');
const requestId = crypto.randomUUID();
const disabled = computed(
  () => !draft.value.trim() || draft.value === props.content || saving.value
);
onMounted(() => dialog.value.open());

async function save() {
  if (disabled.value) return;
  saving.value = true;
  submitted.value = true;
  error.value = '';
  try {
    const { data } = await EditsAPI.edit(
      props.messageId,
      draft.value,
      requestId
    );
    await store.dispatch('updateMessage', data);
    useAlert(t('WHATSAPP_MESSAGING.EDIT_SAVED'));
    saving.value = false;
    dialog.value.close();
  } catch (failure) {
    error.value = t(
      failure.response?.status === 422
        ? 'WHATSAPP_MESSAGING.EDIT_NOT_ALLOWED'
        : 'WHATSAPP_MESSAGING.EDIT_UNCONFIRMED'
    );
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('WHATSAPP_MESSAGING.EDIT')"
    :description="t('WHATSAPP_MESSAGING.EDIT_DESCRIPTION')"
    :confirm-button-label="t('WHATSAPP_MESSAGING.SAVE_EDIT')"
    :cancel-button-label="t('WHATSAPP_CONTACT_CARDS.CANCEL')"
    :disable-confirm-button="disabled"
    :is-loading="saving"
    @confirm="save"
    @close="emit('close')"
  >
    <div class="grid min-w-0 gap-3">
      <label
        for="whatsapp-edit-text"
        class="text-sm font-medium text-n-slate-12"
        >{{ t('WHATSAPP_MESSAGING.EDIT_TEXT') }}</label
      >
      <textarea
        id="whatsapp-edit-text"
        v-model="draft"
        rows="5"
        maxlength="16000"
        dir="auto"
        :readonly="submitted"
        class="min-h-32 w-full resize-y rounded-lg border border-n-weak bg-n-alpha-2 p-3 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/30"
      />
      <p v-if="error" role="alert" class="text-sm text-n-ruby-11">
        {{ error }}
      </p>
    </div>
  </Dialog>
</template>
